import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, User, Award, History, FileText, Target, Star, GraduationCap, CalendarClock, Upload, Trash2, Download, Plus, GitCommitHorizontal, Sparkles, TrendingUp, AlertTriangle, Lightbulb, ArrowRight, RefreshCw, ShieldCheck, IdCard, Camera, Save } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, mediaUrl, uploadFile, uploadImage } from "../../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";

const BASE_TABS = [
  { key: "perfil", label: "Mi perfil", icon: IdCard, selfOnly: true },
  { key: "datos", label: "Datos", icon: User },
  { key: "talento", label: "Talento", icon: Award },
  { key: "ia", label: "Análisis IA", icon: Sparkles, leaderOnly: true },
  { key: "historial", label: "Historial", icon: History },
  { key: "linea", label: "Línea de tiempo", icon: GitCommitHorizontal },
  { key: "documentos", label: "Documentos", icon: FileText },
];

const pInput = "w-full bg-white border border-navy/15 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";
const pLabel = "text-xs font-medium text-navy/60 mb-1 block";
const TZ = ["America/Mexico_City", "America/Tijuana", "America/Cancun", "America/Bogota", "America/Lima", "America/Buenos_Aires", "America/Santiago", "Europe/Madrid", "UTC"];
const FMT = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];

function ProfileTab({ record, onSaved }) {
  const [p, setP] = useState(record?.membership?.profile || {});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file); setP((x) => ({ ...x, avatar: url })); }
    catch { toast.error("No se pudo subir la imagen"); } finally { setUploading(false); }
  };
  const onSignature = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const url = await uploadImage(file);
      await api.put("/people/signature", { image_url: url });
      setP((x) => ({ ...x, firma: url }));
      toast.success("Firma guardada");
    } catch { toast.error("No se pudo subir la firma"); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/people/record/profile", {
        avatar: p.avatar || null, telefono: p.telefono || "", extension: p.extension || "",
        biografia: p.biografia || "", responsabilidades: p.responsabilidades || "",
        zona_horaria: p.zona_horaria || "", idioma: p.idioma || "", formato_fecha: p.formato_fecha || "",
        fecha_nacimiento: p.fecha_nacimiento || null,
      });
      toast.success("Perfil actualizado");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="record-perfil">
      <div className="bg-white border border-navy/10 rounded-2xl p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <Avatar className="h-20 w-20">
              {p.avatar ? <AvatarImage src={mediaUrl(p.avatar)} alt={record.name} /> : null}
              <AvatarFallback className="text-lg bg-violet/10 text-violet">{(record.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
            </Avatar>
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-navy text-cream flex items-center justify-center cursor-pointer hover:bg-navy-soft" data-testid="profile-photo-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
            </label>
          </div>
          <div>
            <div className="text-lg font-medium text-navy">{record.name}</div>
            <div className="text-sm text-navy/50">{[record.puesto, record.area].filter(Boolean).join(" · ") || "—"}</div>
            <div className="text-xs text-navy/40">{record.email}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={pLabel}>Teléfono</label><input data-testid="profile-telefono" className={pInput} value={p.telefono || ""} onChange={(e) => setP({ ...p, telefono: e.target.value })} /></div>
          <div><label className={pLabel}>Extensión</label><input data-testid="profile-extension" className={pInput} value={p.extension || ""} onChange={(e) => setP({ ...p, extension: e.target.value })} /></div>
          <div><label className={pLabel}>Zona horaria</label><select data-testid="profile-tz" className={pInput} value={p.zona_horaria || ""} onChange={(e) => setP({ ...p, zona_horaria: e.target.value })}><option value="">Selecciona…</option>{TZ.map((z) => <option key={z} value={z}>{z}</option>)}</select></div>
          <div><label className={pLabel}>Idioma</label><select data-testid="profile-idioma" className={pInput} value={p.idioma || ""} onChange={(e) => setP({ ...p, idioma: e.target.value })}><option value="">Selecciona…</option><option value="es">Español</option><option value="en">English</option></select></div>
          <div><label className={pLabel}>Formato de fecha</label><select data-testid="profile-formato" className={pInput} value={p.formato_fecha || ""} onChange={(e) => setP({ ...p, formato_fecha: e.target.value })}><option value="">Selecciona…</option>{FMT.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div><label className={pLabel}>Fecha de nacimiento</label><input type="date" data-testid="profile-dob" className={pInput} value={(p.fecha_nacimiento || "").slice(0, 10)} onChange={(e) => setP({ ...p, fecha_nacimiento: e.target.value })} /></div>
          <div className="col-span-2"><label className={pLabel}>Biografía</label><textarea data-testid="profile-bio" rows={3} className={pInput} value={p.biografia || ""} onChange={(e) => setP({ ...p, biografia: e.target.value })} /></div>
          <div className="col-span-2"><label className={pLabel}>Responsabilidades</label><textarea rows={2} className={pInput} value={p.responsabilidades || ""} onChange={(e) => setP({ ...p, responsabilidades: e.target.value })} /></div>
        </div>
        <div className="flex justify-end mt-5">
          <button data-testid="profile-save" onClick={save} disabled={saving} className="btn btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios</button>
        </div>
      </div>
      <div className="bg-white border border-navy/10 rounded-2xl p-6" data-testid="signature-box">
        <div className="text-sm font-medium text-navy mb-1 flex items-center gap-2"><FileText className="w-4 h-4" /> Mi firma electrónica</div>
        <p className="text-xs text-navy/50 mb-3">Sube tu firma en PNG (fondo transparente). Se usará al firmar documentos con OMNIA Sign.</p>
        <div className="flex items-center gap-4">
          <div className="w-48 h-24 border border-dashed border-navy/20 rounded-lg flex items-center justify-center bg-navy/[0.02]">
            {p.firma ? <img src={mediaUrl(p.firma)} alt="firma" className="max-h-20 max-w-44 object-contain" data-testid="signature-preview" /> : <span className="text-xs text-navy/35">Sin firma</span>}
          </div>
          <label className="btn btn-ghost cursor-pointer" data-testid="signature-upload">Subir firma PNG<input type="file" accept="image/png" className="hidden" onChange={onSignature} /></label>
        </div>
      </div>
    </div>
  );
}

const RISK_TONE = {
  bajo: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", label: "Riesgo de rotación bajo" },
  medio: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200", label: "Riesgo de rotación medio" },
  alto: { bg: "bg-red-50", text: "text-red-600", border: "border-red-200", label: "Riesgo de rotación alto" },
};

function AIList({ title, items, icon: Icon, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`ai-list-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`flex items-center gap-2 text-sm font-medium mb-3 ${tone}`}><Icon className="w-4 h-4" /> {title}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-sm text-navy/70"><span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${tone.replace("text-", "bg-")}`} /> {it}</li>
        ))}
      </ul>
    </div>
  );
}

function AITab({ targetId, name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback((refresh = false) => {
    if (!targetId) return;
    refresh ? setRegen(true) : setLoading(true);
    setErr("");
    api.get(`/people/record/${targetId}/ai-analysis${refresh ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "No se pudo generar el análisis"))
      .finally(() => { setLoading(false); setRegen(false); });
  }, [targetId]);
  useEffect(() => { load(false); }, [load]);

  if (loading) return <div className="py-12 flex flex-col items-center gap-3 text-navy/45" data-testid="ai-loading"><Loader2 className="w-6 h-6 animate-spin text-violet" /><span className="text-sm">La IA está analizando el expediente…</span></div>;
  if (err) return (
    <div className="bg-white border border-navy/10 rounded-2xl p-6 text-center" data-testid="ai-error">
      <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
      <p className="text-sm text-navy/55 mb-4">{err}</p>
      <button onClick={() => load(true)} className="btn btn-primary gap-2" data-testid="ai-retry"><RefreshCw className="w-4 h-4" /> Reintentar</button>
    </div>
  );
  if (!data) return null;
  const risk = RISK_TONE[data.riesgo_rotacion] || RISK_TONE.medio;

  return (
    <div className="space-y-5" data-testid="record-ia">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${risk.bg} ${risk.text} ${risk.border}`} data-testid="ai-risk-badge">
          <ShieldCheck className="w-4 h-4" /> {risk.label}
        </div>
        <button onClick={() => load(true)} disabled={regen} className="btn btn-ghost gap-2 text-sm" data-testid="ai-regenerate">
          {regen ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerar
        </button>
      </div>

      <div className="bg-gradient-to-br from-violet/5 to-transparent border border-violet/15 rounded-2xl p-5" data-testid="ai-summary">
        <div className="flex items-center gap-2 text-sm font-medium text-violet mb-2"><Sparkles className="w-4 h-4" /> Resumen inteligente · {name}</div>
        <p className="text-sm text-navy/75 leading-relaxed">{data.resumen}</p>
        {data.riesgo_rotacion_motivo && <p className="text-xs text-navy/45 mt-2">Motivo del nivel de riesgo: {data.riesgo_rotacion_motivo}</p>}
      </div>

      {(data.alertas || []).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5" data-testid="ai-alerts">
          <div className="flex items-center gap-2 text-sm font-medium text-red-600 mb-3"><AlertTriangle className="w-4 h-4" /> Alertas</div>
          <ul className="space-y-2">{data.alertas.map((a, i) => <li key={i} className="flex gap-2 text-sm text-red-700"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" /> {a}</li>)}</ul>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <AIList title="Fortalezas" items={data.fortalezas} icon={TrendingUp} tone="text-emerald-600" />
        <AIList title="Riesgos" items={data.riesgos} icon={AlertTriangle} tone="text-amber-600" />
        <AIList title="Recomendaciones" items={data.recomendaciones} icon={Lightbulb} tone="text-violet" />
        <AIList title="Próximas acciones" items={data.proximas_acciones} icon={ArrowRight} tone="text-navy/70" />
        <AIList title="Oportunidades" items={data.oportunidades} icon={Sparkles} tone="text-gold" />
      </div>

      <p className="text-xs text-navy/35 flex items-center gap-1.5" data-testid="ai-footer">
        <Sparkles className="w-3 h-3" /> Generado con IA a partir de datos objetivos del expediente. {data.ai === false && "(análisis IA no disponible)"} La decisión final es humana.
      </p>
    </div>
  );
}

const EVENT_LABELS = { promocion: "Promoción", transferencia: "Transferencia", cambio_salario: "Cambio salarial", licencia: "Licencia / ausencia", reconocimiento: "Reconocimiento", baja: "Baja", nota: "Nota" };

function TimelineTab({ targetId, name }) {
  const [tl, setTl] = useState(null);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "nota", title: "", effective_date: "", note: "", details: {} });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/lifecycle/timeline/${targetId}`).then((r) => setTl(r.data)).catch(() => {}); }, [targetId]);
  useEffect(() => { api.get("/lifecycle/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (targetId) load(); }, [targetId, load]);
  const submit = async () => {
    if (!f.title.trim() && f.type === "nota") return toast.error("Escribe un título");
    setBusy(true);
    try {
      const details = {};
      if (f.type === "promocion" || f.type === "transferencia") { if (f.puesto) details.puesto = f.puesto; if (f.area) details.area = f.area; }
      if (f.type === "cambio_salario" && f.tarifa) { details.tarifa = Number(f.tarifa); details.tipo_tarifa = "por_hora"; }
      await api.post("/lifecycle/event", { user_id: targetId, type: f.type, title: f.title, effective_date: f.effective_date, note: f.note, details });
      toast.success("Evento registrado"); setOpen(false); setF({ type: "nota", title: "", effective_date: "", note: "", details: {} }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  if (!tl) return <div className="py-10 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="record-linea">
      {meta?.is_leader && <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="lc-add-event"><Plus className="w-4 h-4" /> Registrar evento</button>}
      {tl.items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin eventos en la línea de tiempo.</div>
        : <div className="relative pl-5 space-y-4 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-navy/10">
            {tl.items.map((it, i) => (
              <div key={i} className="relative" data-testid={`lc-item-${i}`}>
                <span className={`absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full ${it.kind === "event" ? "bg-violet" : it.kind === "process" ? "bg-gold" : "bg-navy/30"}`} />
                <div className="bg-white border border-navy/10 rounded-xl p-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium text-navy">{it.title || it.label}</span><span className="text-xs text-navy/40">{(it.at || "").slice(0, 10)}</span></div>
                  <div className="text-xs text-navy/45">{it.label}{it.by_name ? ` · ${it.by_name}` : ""}</div>
                  {(it.changes || []).map((c, j) => <div key={j} className="text-xs text-navy/55 mt-0.5">{c.label}: <span className="text-navy/40">{c.from}</span> → <span className="text-navy font-medium">{c.to}</span></div>)}
                  {it.note && <p className="text-sm text-navy/55 mt-1">{it.note}</p>}
                </div>
              </div>
            ))}
          </div>}
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h3 className="font-editorial text-2xl mb-4">Registrar evento · {name}</h3>
            <div className="space-y-3">
              <select className="field" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="lc-event-type">
                {(meta?.event_types || []).map((t) => <option key={t} value={t}>{EVENT_LABELS[t] || t}</option>)}
              </select>
              <input className="field" placeholder="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="lc-event-title" />
              <input type="date" className="field" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="lc-event-date" />
              {(f.type === "promocion" || f.type === "transferencia") && <div className="grid grid-cols-2 gap-3"><input className="field" placeholder="Nuevo puesto" value={f.puesto || ""} onChange={(e) => setF({ ...f, puesto: e.target.value })} /><input className="field" placeholder="Nueva área" value={f.area || ""} onChange={(e) => setF({ ...f, area: e.target.value })} /></div>}
              {f.type === "cambio_salario" && <input type="number" className="field" placeholder="Nueva tarifa por hora" value={f.tarifa || ""} onChange={(e) => setF({ ...f, tarifa: e.target.value })} />}
              <textarea className="field" rows={2} placeholder="Nota / motivo" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={submit} disabled={busy} className="btn btn-primary" data-testid="lc-event-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4 py-2 border-b border-navy/5 last:border-0"><span className="text-navy/45 text-sm">{k}</span><span className="text-sm text-navy font-medium text-right">{v || "—"}</span></div>;
}
function Section({ title, icon: Icon, children, count }) {
  return (
    <div className="bg-white border border-navy/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 text-sm font-medium text-navy/70 mb-3"><Icon className="w-4 h-4" /> {title}{count != null && <Badge variant="outline" className="border-navy/15 text-navy/45">{count}</Badge>}</div>
      {children}
    </div>
  );
}

export default function MasterRecord() {
  const { uid } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(uid ? "datos" : "perfil");
  const [loading, setLoading] = useState(true);
  const [targetId, setTargetId] = useState(null);
  const [isLeader, setIsLeader] = useState(false);
  const [docCat, setDocCat] = useState("contrato");
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => { api.get("/lifecycle/meta").then((r) => setIsLeader(!!r.data?.is_leader)).catch(() => {}); }, []);
  const isSelf = !uid;
  const TABS = BASE_TABS.filter((t) => (!t.leaderOnly || isLeader) && (!t.selfOnly || isSelf));

  const load = useCallback(() => {
    setLoading(true);
    if (!uid) {
      api.get("/people/context").then((c) => { setTargetId(c.data.user_id); return api.get(`/people/record/${c.data.user_id}/full`); })
        .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    } else {
      setTargetId(uid);
      api.get(`/people/record/${uid}/full`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const onUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !targetId) return;
    setUploadingDoc(true);
    try {
      const up = await uploadFile(file, true);
      await api.post("/people/documents", { user_id: targetId, name: file.name, category: docCat, file_path: up.path, filename: up.filename, content_type: up.content_type });
      toast.success("Documento agregado");
      load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Error al subir"); }
    finally { setUploadingDoc(false); e.target.value = ""; }
  };
  const delDoc = async (id) => { try { await api.delete(`/people/documents/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("Error"); } };

  if (loading) return <OSLayout title="Expediente"><div className="flex items-center gap-2 text-navy/40 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Cargando expediente…</div></OSLayout>;
  if (!data) return <OSLayout title="Expediente"><div className="text-navy/40 py-10">No disponible o fuera de tu alcance.</div></OSLayout>;

  const r = data.record; const m = r.membership || {}; const p = m.profile || {}; const t = data.talent;

  return (
    <OSLayout title={isSelf ? "OMNIA User" : "Expediente del Colaborador"}>
      <div className="max-w-4xl space-y-6" data-testid="master-record">
        <div className="bg-white border border-navy/10 rounded-2xl p-5 flex items-center gap-4">
          <Avatar className="h-16 w-16">
            {r.avatar ? <AvatarImage src={mediaUrl(r.avatar)} alt={r.name} /> : null}
            <AvatarFallback className="text-lg bg-violet/10 text-violet">{(r.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-xl font-medium text-navy">{r.name}</div>
            <div className="text-sm text-navy/55">{[r.puesto, r.area].filter(Boolean).join(" · ") || "—"}</div>
            <div className="text-xs text-navy/40">{r.email}</div>
          </div>
          {r.es_lider && <Badge className="bg-gold/15 text-gold border-none">Líder</Badge>}
        </div>

        <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-2">
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`record-tab-${tb.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${tab === tb.key ? "bg-navy text-cream" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "perfil" && isSelf && <ProfileTab record={r} onSaved={load} />}

        {tab === "datos" && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="record-datos">
            <Section title="Organización" icon={User}>
              <Row k="Unidad de Negocio" v={m.unidad_negocio} /><Row k="Dirección" v={m.direccion} /><Row k="Gerencia" v={m.gerencia} />
              <Row k="Área" v={m.area} /><Row k="Departamento" v={m.departamento} /><Row k="Equipo" v={m.equipo} />
              <Row k="Puesto" v={m.puesto} /><Row k="Nivel jerárquico" v={m.nivel_jerarquico} /><Row k="Reporta a" v={r.reporta_a_nombre} />
              <Row k="Ruta organizacional" v={m.ruta_organizacional} />
            </Section>
            <Section title="Contacto y perfil" icon={User}>
              <Row k="Teléfono" v={p.telefono} /><Row k="Extensión" v={p.extension} /><Row k="Zona horaria" v={p.zona_horaria} />
              <Row k="Idioma" v={p.idioma} /><Row k="Cumpleaños" v={(p.fecha_nacimiento || "").slice(0, 10)} /><Row k="Estado" v={r.estado} />
              {p.biografia && <div className="mt-3 text-sm text-navy/60">{p.biografia}</div>}
            </Section>
          </div>
        )}

        {tab === "talento" && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="record-talento">
            <Section title="Objetivos" icon={Target} count={t.objectives.length}>
              {t.objectives.length === 0 ? <p className="text-sm text-navy/35">Sin objetivos.</p> :
                t.objectives.slice(0, 6).map((o, i) => <div key={i} className="py-1.5 border-b border-navy/5 last:border-0 text-sm"><span className="text-navy">{o.title || o.name || "Objetivo"}</span> <span className="text-navy/40 text-xs">· {o.progress ?? 0}%</span></div>)}
            </Section>
            <Section title="Evaluaciones" icon={Star} count={t.reviews.length}>
              {t.reviews.length === 0 ? <p className="text-sm text-navy/35">Sin evaluaciones.</p> :
                t.reviews.slice(0, 6).map((rv, i) => <div key={i} className="py-1.5 border-b border-navy/5 last:border-0 text-sm">{rv.period || rv.type || "Evaluación"} <span className="text-navy/40 text-xs">· {rv.score ?? rv.rating ?? "—"}</span></div>)}
            </Section>
            <Section title="Competencias" icon={Award} count={t.skills.length}>
              {t.skills.length === 0 ? <p className="text-sm text-navy/35">Sin competencias.</p> :
                <div className="flex flex-wrap gap-2">{t.skills.map((s, i) => <Badge key={i} className="badge-violet">{s.skill} · {s.level}/5</Badge>)}</div>}
            </Section>
            <Section title="Aprendizaje" icon={GraduationCap} count={t.learning.length}>
              {t.learning.length === 0 ? <p className="text-sm text-navy/35">Sin cursos asignados.</p> :
                t.learning.slice(0, 6).map((l, i) => <div key={i} className="py-1.5 border-b border-navy/5 last:border-0 text-sm">{l.course_title || l.title || "Curso"} <span className="text-navy/40 text-xs">· {l.status || "—"}</span></div>)}
            </Section>
          </div>
        )}

        {tab === "historial" && (
          <div className="space-y-4" data-testid="record-historial">
            <Section title="Cambios en la ficha" icon={History} count={data.history.events.length}>
              {data.history.events.length === 0 ? <p className="text-sm text-navy/35">Sin cambios registrados.</p> :
                <div className="relative pl-4 border-l-2 border-violet/20 space-y-4">
                  {data.history.events.map((ev, i) => (
                    <div key={i} className="relative">
                      <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-violet" />
                      <div className="text-xs text-navy/40">{(ev.at || "").slice(0, 16).replace("T", " ")} · {ev.by_name || "—"}</div>
                      {(ev.changes || []).map((c, j) => (
                        <div key={j} className="text-sm text-navy">{c.label}: <span className="text-navy/45">{c.from}</span> → <b>{c.to}</b></div>
                      ))}
                    </div>
                  ))}
                </div>}
            </Section>
            <Section title="Solicitudes de tiempo libre" icon={CalendarClock} count={data.history.timeoff.length}>
              {data.history.timeoff.length === 0 ? <p className="text-sm text-navy/35">Sin registros.</p> :
                <div className="divide-y divide-navy/5">
                  {data.history.timeoff.map((to, i) => (
                    <div key={i} className="py-2 flex items-center justify-between text-sm">
                      <span className="text-navy">{to.type} · {to.days}d <span className="text-navy/40 text-xs">{to.start?.slice(0, 10)} → {to.end?.slice(0, 10)}</span></span>
                      <Badge variant="outline" className={to.status === "approved" ? "border-emerald-300 text-emerald-600" : to.status === "rejected" ? "border-red-300 text-red-500" : "border-navy/20 text-navy/50"}>{to.status}</Badge>
                    </div>
                  ))}
                </div>}
            </Section>
          </div>
        )}

        {tab === "linea" && <TimelineTab targetId={targetId} name={r.name} />}

        {tab === "ia" && isLeader && <AITab targetId={targetId} name={r.name} />}

        {tab === "documentos" && (
          <Section title="Documentos" icon={FileText} count={data.documents.length}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <select value={docCat} onChange={(e) => setDocCat(e.target.value)} data-testid="doc-category"
                className="bg-white border border-navy/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
                {["contrato", "ine", "comprobante", "cv", "titulo", "otro"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="btn btn-primary cursor-pointer gap-2" data-testid="doc-upload">
                {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir documento
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" className="hidden" onChange={onUploadDoc} />
              </label>
            </div>
            {data.documents.length === 0 ? <p className="text-sm text-navy/35" data-testid="record-documentos">Sin documentos cargados.</p> :
              <div className="divide-y divide-navy/5" data-testid="record-documentos">
                {data.documents.map((d) => (
                  <div key={d.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`doc-${d.id}`}>
                    <FileText className="w-4 h-4 text-navy/30 shrink-0" />
                    <div className="min-w-0 flex-1"><div className="text-navy truncate">{d.name}</div><div className="text-xs text-navy/40">{d.category} · {(d.at || "").slice(0, 10)}</div></div>
                    <a href={mediaUrl(d.file_path)} target="_blank" rel="noreferrer" className="text-navy/40 hover:text-violet"><Download className="w-4 h-4" /></a>
                    <button onClick={() => delDoc(d.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>}
          </Section>
        )}
      </div>
    </OSLayout>
  );
}
